import { useCallback, useEffect, useRef, useState } from 'react';

import { OwnedInvestment, ProjectStatus } from 'api-client';
import { Filter } from 'components/pages/projects/filter/ProjectsFilter/ProjectsFilter';
import { createInvestmentManager } from 'di';
import { useErrorCallback } from 'hooks';

const INITIAL_PER_PAGE = 5;

export const useMainAccountInvestments = (accountUuid: string | undefined) => {
  const handleError = useErrorCallback();
  const handleErrorRef = useRef(handleError);

  const [items, setItems] = useState<OwnedInvestment[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<ProjectStatus[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    handleErrorRef.current = handleError;
  }, [handleError]);

  const loadInvestments = useCallback(
    async (pageToLoad: number, append: boolean) => {
      if (!accountUuid) {
        return;
      }

      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      try {
        const manager = await createInvestmentManager();
        const filter: Filter = {
          search: '',
          initialTermFrom: null,
          initialTermTo: null,
          interestRateFrom: null,
          interestRateTo: null,
          simpleInterestRateFrom: null,
          simpleInterestRateTo: null,
          statuses: statusFilter,
          accountUuid,
        };
        const response = await manager.getListOwnedInvestments(
          pageToLoad,
          INITIAL_PER_PAGE,
          filter
        );

        setTotalCount(response.totalCount);
        setPage(pageToLoad);
        setItems((currentItems) =>
          append ? [...currentItems, ...response.items] : response.items
        );
      } catch (error) {
        if (error instanceof Error) {
          await handleErrorRef.current(error);
        }
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [accountUuid, statusFilter]
  );

  useEffect(() => {
    if (!accountUuid) {
      setItems([]);
      setTotalCount(0);
      return;
    }

    void loadInvestments(1, false);
  }, [accountUuid, statusFilter, loadInvestments]);

  const loadMore = useCallback(() => {
    if (loading || loadingMore || items.length >= totalCount) {
      return;
    }

    void loadInvestments(page + 1, true);
  }, [items.length, loadInvestments, loading, loadingMore, page, totalCount]);

  const changeStatusFilter = useCallback(
    (statuses: ProjectStatus[]) => {
      if (loading || loadingMore) {
        return;
      }

      setLoading(true);
      setPage(1);
      setStatusFilter(statuses);
    },
    [loading, loadingMore]
  );

  const hasMore = items.length < totalCount;

  return {
    items,
    totalCount,
    loading,
    loadingMore,
    statusFilter,
    changeStatusFilter,
    loadMore,
    hasMore,
  };
};
